import React from 'react';
import { CalendarDays, FileText, Hash, Tags, Type } from 'lucide-react';
import { UploadedDataset } from '../../services/analytics';

interface DataPreviewProps {
  dataset: UploadedDataset;
}

const DataPreview: React.FC<DataPreviewProps> = ({ dataset }) => {
  const containerStyle: React.CSSProperties = {
    overflowX: 'auto',
    marginTop: '2rem',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
  };

  const cellStyle: React.CSSProperties = {
    padding: '1rem',
    borderBottom: '1px solid var(--border-color)',
    fontSize: '0.875rem',
  };

  const headerStyle: React.CSSProperties = {
    ...cellStyle,
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
    textTransform: 'uppercase',
  };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '1rem', marginTop: '2rem' }}>
        {dataset.profiles.map((profile) => (
          <div key={profile.name} className="premium-card" style={{ padding: '1rem', display: 'flex', gap: '0.8rem', alignItems: 'flex-start' }}>
            <span className="icon-tile" style={{ width: '34px', height: '34px', flex: '0 0 auto' }}>
              {renderProfileIcon(profile.role)}
            </span>
            <div style={{ minWidth: 0 }}>
              <span className="mono-label" style={{ marginBottom: '0.35rem' }}>{profile.role}</span>
              <strong style={{ display: 'block', marginBottom: '0.45rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile.name}</strong>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              {profile.type} · {profile.uniqueCount.toLocaleString()} unique values
            </p>
            </div>
          </div>
        ))}
      </div>

      <div className="premium-card" style={containerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {dataset.columns.map((column) => (
                <th key={column} style={headerStyle}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataset.rows.slice(0, 8).map((row, rowIndex) => (
              <tr key={rowIndex}>
                {dataset.columns.map((column) => (
                  <td key={column} style={cellStyle}>{formatCell(row[column])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const renderProfileIcon = (role: UploadedDataset['profiles'][number]['role']) => {
  switch (role) {
    case 'date':
      return <CalendarDays size={17} />;
    case 'metric':
      return <Hash size={17} />;
    case 'category':
      return <Tags size={17} />;
    case 'identifier':
      return <FileText size={17} />;
    default:
      return <Type size={17} />;
  }
};

const formatCell = (value: UploadedDataset['rows'][number][string]) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

export default DataPreview;
